// Story 1.22 — TanStack mutation for PATCH /api/v1/creators/me.
//
// Fresh `crypto.randomUUID()` idempotency key per submit. Preserves
// the per-link `failures` and the `reason` discriminator on errors
// so the form can highlight specific rows on a 422 or route the user
// to the appropriate card on a 403.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

import type {
  CreatorProfileEnvelope,
  LinkedAccount,
} from "./useCreatorProfile";

export type UpdateCreatorProfileRequest = {
  display_name?: string;
  hello_text?: string;
  bio?: string | null;
  image_blob_path?: string;
  linked_accounts?: { kind: string; url: string }[];
  region?: string;
  language?: string;
  portfolio_auto_assign_opt_out?: boolean;
};

export type UpdateCreatorProfileError = {
  code: string;
  message: string;
  reason?: string;
  field?: string;
  failures?: { index: number; kind: string; url: string; reason: string }[];
};

async function patchCreatorProfile(
  body: UpdateCreatorProfileRequest,
  idempotencyKey: string,
): Promise<CreatorProfileEnvelope> {
  const response = await fetch("/api/v1/creators/me", {
    method: "PATCH",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": idempotencyKey,
    },
    body: JSON.stringify(body),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: UpdateCreatorProfileError = {
      code: "CREATOR_PROFILE_UPDATE_FAILED",
      message: `Update failed (HTTP ${response.status})`,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = { ...raw.error };
      }
    } catch {
      // Body not JSON.
    }
    throw detail;
  }
  return (await response.json()) as CreatorProfileEnvelope;
}

export function useUpdateCreatorProfile() {
  const queryClient = useQueryClient();
  return useMutation<
    CreatorProfileEnvelope,
    UpdateCreatorProfileError,
    UpdateCreatorProfileRequest
  >({
    mutationFn: (body) => patchCreatorProfile(body, crypto.randomUUID()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["creator-profile-me"] });
    },
  });
}

export type { LinkedAccount };
